import React, { useState, useEffect, useRef } from 'react';
import { Container, Button, Alert, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import JsBarcode from 'jsbarcode';
import api from '../../api';

const SKUGenerator = () => {
  const navigate = useNavigate();

  const [sku, setSku] = useState('');
  const [manualSku, setManualSku] = useState('');
  const [error, setError] = useState(null);
  const [bulkCount, setBulkCount] = useState(1);
  const [labelQty, setLabelQty] = useState(1); // ⭐ NEW: quantity per SKU
  const [bulkList, setBulkList] = useState([]);
  const [openingDraft, setOpeningDraft] = useState(false); // NEW
  const barcodeRef = useRef(null);

  // ---------------------------------------------------------
  // SINGLE SKU barcode render + save
  // ---------------------------------------------------------
  useEffect(() => {
    if (sku && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, sku, {
          format: 'CODE128',
          width: 2,
          height: 50,
          displayValue: false,
          margin: 0
        });

        const dataUrl = barcodeRef.current.toDataURL('image/png');

        // Save + auto draft create
        api.post('/tools/save-barcode', { sku, barcode: dataUrl })
          .catch(err => setError('Failed to save barcode'));

      } catch (err) {
        setError('Failed to render barcode');
      }
    }
  }, [sku]);

  const isValidCode128 = (value) => /^[A-Za-z0-9 -]+$/.test(value);
  const sanitizeSKU = (value) => value.trim().replace(/[^\x20-\x7E]/g, '');

  const submitManualSku = () => {
    const sanitized = sanitizeSKU(manualSku);

    if (!isValidCode128(sanitized)) {
      setError('Invalid SKU: Only letters, numbers, spaces, and hyphens are allowed.');
      return;
    }

    // This triggers your existing useEffect → /tools/save-barcode
    setSku(sanitized);

    setManualSku('');
    setError(null);
  };


  // ---------------------------------------------------------
  // SINGLE SKU GENERATION
  // ---------------------------------------------------------
  const generate = async () => {
    try {
      const response = await api.get('/tools/sku');
      setSku(response.data.sku);
      setError(null);
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setError(`Failed to generate SKU: ${msg}`);
      setSku('');
    }
  };

  // ---------------------------------------------------------
  // NEW: GENERATE SKU + CREATE DRAFT + OPEN DRAFT
  // ---------------------------------------------------------
  const generateAndOpenDraft = async () => {
    try {
      setOpeningDraft(true);
      setError(null);

      // Generate SKU
      const response = await api.get('/tools/sku');
      const newSku = response.data.sku;

      // Create barcode in memory
      const canvas = document.createElement('canvas');

      JsBarcode(canvas, newSku, {
        format: 'CODE128',
        width: 2,
        height: 50,
        displayValue: false,
        margin: 0
      });

      const barcodeDataUrl = canvas.toDataURL('image/png');

      // Save barcode + create draft
      const saveResponse = await api.post('/tools/save-barcode', {
        sku: newSku,
        barcode: barcodeDataUrl
      });

      const draftId = saveResponse.data.draftId;

      if (!draftId) {
        throw new Error('Draft was created but no draft ID was returned.');
      }

      // Open the newly created draft
      navigate(`/inventory/drafts/edit/${draftId}`);

    } catch (err) {
      const msg =
        err.response?.data?.details ||
        err.response?.data?.error ||
        err.message;

      setError(`Failed to generate SKU and open draft: ${msg}`);
      setOpeningDraft(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(sku);
    alert('SKU copied to clipboard!');
  };

  const printLabel = () => printSingleLabel(sku);

  // Print one label
  const printSingleLabel = (skuValue) => {
    const win = window.open('', '_blank');
    win.document.write(`
      <html>
        <head>
          <title>Print Label</title>
          <style>
            @media print {
              body { margin: 0; padding: 0; }
              .label {
                width: 2in;
                height: 1in;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                font-family: Arial, sans-serif;
              }
              .barcode { width: 100%; height: 0.6in; }
              .sku-text { font-size: 12px; margin-top: 4px; }
              @page { size: 2in 1in; margin: 0; }
            }
          </style>
        </head>
        <body>
          <div class="label">
            <canvas id="print-barcode" class="barcode"></canvas>
            <div class="sku-text">${skuValue}</div>
          </div>
        </body>
      </html>
    `);

    win.document.close();

    JsBarcode(win.document.getElementById('print-barcode'), skuValue, {
      format: 'CODE128',
      width: 2,
      height: 50,
      displayValue: false,
      margin: 0
    });

    win.focus();
    win.print();
    win.close();
  };

  // ---------------------------------------------------------
  // BULK SKU GENERATION (auto draft via /save-barcode)
  // ---------------------------------------------------------
  const generateBulk = async () => {
    try {
      setBulkList([]);
      let list = [];

      for (let i = 0; i < bulkCount; i++) {
        const response = await api.get('/tools/sku');
        const newSku = response.data.sku;

        const canvas = document.createElement('canvas');
        JsBarcode(canvas, newSku, {
          format: 'CODE128',
          width: 2,
          height: 50,
          displayValue: false
        });

        const barcodeDataUrl = canvas.toDataURL('image/png');

        await api.post('/tools/save-barcode', {
          sku: newSku,
          barcode: barcodeDataUrl
        });

        list.push({ sku: newSku, barcodeDataUrl });
      }

      setBulkList(list);
      setError(null);

    } catch (err) {
      setError('Failed to generate bulk SKUs');
    }
  };

  // ---------------------------------------------------------
  // BULK PRINT (with qty)
  // ---------------------------------------------------------
  const printAllBulk = () => {
    const win = window.open('', '_blank');

    let labelsHtml = '';

    bulkList.forEach(item => {
      for (let i = 0; i < labelQty; i++) {
        labelsHtml += `
          <div class="label">
            <img class="barcode-img" src="${item.barcodeDataUrl}" style="width:100%;height:50px;" />
            <div class="sku-text">${item.sku}</div>
          </div>
        `;
      }
    });

    win.document.write(`
      <html>
        <head>
          <title>Print Bulk Labels</title>
          <style>
            @media print {
              body { margin: 0; padding: 0; }
              .label {
                width: 2in;
                height: 1in;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                page-break-after: always;
                font-family: Arial, sans-serif;
              }
              .sku-text { margin-top: 4px; font-size: 12px; }
              @page { size: 2in 1in; margin: 0; }
            }
          </style>
        </head>
        <body>${labelsHtml}</body>
      </html>
    `);

    win.document.close();

    // ⭐ Chrome-safe: wait until popup DOM is fully ready
    const checkReady = setInterval(() => {
      if (win.document.readyState === "complete") {
        clearInterval(checkReady);

        // Now wait for ALL barcode images to load
        const images = win.document.querySelectorAll('.barcode-img');
        let loaded = 0;

        images.forEach(img => {
          if (img.complete) {
            loaded++;
          } else {
            img.onload = () => {
              loaded++;
            };
          }
        });

        const waitForImages = setInterval(() => {
          if (loaded === images.length) {
            clearInterval(waitForImages);

            setTimeout(() => {
              win.focus();
              win.print();
              win.close();
            }, 150);
          }
        }, 50);
      }
    }, 50);
  };

  // ---------------------------------------------------------
  // RENDER UI
  // ---------------------------------------------------------
  return (
    <Container className="mt-5">
      <h2 className="mb-4">SKU Generator</h2>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* SINGLE SKU */}
      <Button
        variant="primary"
        onClick={generate}
        className="mb-3 me-2"
        disabled={openingDraft}
      >
        Generate SKU
      </Button>

      {/* NEW BUTTON */}
      <Button
        variant="success"
        onClick={generateAndOpenDraft}
        className="mb-3"
        disabled={openingDraft}
      >
        {openingDraft ? 'Creating Draft...' : 'Generate SKU & Open Draft'}
      </Button>

      <h4 className="mt-4">Or Enter SKU Manually</h4>

      <Form.Group className="mb-3" style={{ maxWidth: '300px' }}>
        <Form.Label>Manual SKU</Form.Label>
        <Form.Control
          type="text"
          placeholder="Enter existing SKU"
          value={manualSku}
          onChange={(e) => setManualSku(e.target.value.toUpperCase())}
        />
      </Form.Group>

      <Button variant="warning" onClick={submitManualSku} className="mb-3">
        Import Manual SKU
      </Button>


      {sku && (
        <div>
          <p>Generated SKU: {sku}</p>
          <canvas ref={barcodeRef} className="mb-3"></canvas>

          <Button variant="secondary" onClick={copyToClipboard} className="me-2">
            Copy to Clipboard
          </Button>

          <Button variant="success" onClick={printLabel}>
            Print Label
          </Button>
        </div>
      )}

      <hr className="my-4" />

      {/* BULK GENERATION */}
      <h4>Bulk Generate SKUs</h4>

      <Form.Group className="mb-3" style={{ maxWidth: '200px' }}>
        <Form.Label>How many SKUs?</Form.Label>
        <Form.Control
          type="number"
          min="1"
          value={bulkCount}
          onChange={(e) => setBulkCount(parseInt(e.target.value))}
        />
      </Form.Group>

      {/* ⭐ NEW QTY INPUT */}
      <Form.Group className="mb-3" style={{ maxWidth: '200px' }}>
        <Form.Label>Labels per SKU</Form.Label>
        <Form.Control
          type="number"
          min="1"
          value={labelQty}
          onChange={(e) => setLabelQty(parseInt(e.target.value))}
        />
      </Form.Group>

      <Button variant="dark" onClick={generateBulk} className="mb-3">
        Generate Bulk SKUs
      </Button>

      {bulkList.length > 0 && (
        <>
          <div className="mt-3">
            <h5>Generated SKUs</h5>
            {bulkList.map(item => (
              <div key={item.sku} style={{ marginBottom: '10px' }}>
                <img src={item.barcodeDataUrl} alt="barcode" />
                <div>{item.sku}</div>
              </div>
            ))}
          </div>

          <Button variant="success" className="mt-3" onClick={printAllBulk}>
            Print All Labels
          </Button>
        </>
      )}
    </Container>
  );
};

export default SKUGenerator;